/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ButtonContainer, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import ModalButton from 'components/Modal/Button'
import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
import ErrorMessage from 'components/ErrorMessage'
import Spinner from 'components/Spinner'
import Textarea from 'components/Textarea'
import RadioInputs from 'components/RadioInputs'

import { scrubNull } from 'helpers/formatters'

import useDealer from 'hooks/useDealer'
import { useSessionContext } from 'hooks/SessionContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import useEmailUnsubscribeStatus from 'hooks/useEmailUnsubscribeStatus'

import Api from 'services/api'
import { DeviceBase } from 'types/Device'
import { FormData } from './SystemReportsModal'
import useScreenSize from 'hooks/useScreenSize'
import { scrubText } from 'helpers/inputCleaners'

type Props = {
  selectEmailSystemReport: () => void
  resetAndCloseModal: () => void
  devices: Array<DeviceBase>
  errors?: { devices?: string, firstName?: string, lastName?: string }
  handleGenerateReport: (imageId: string) => void,
  handleFormData: (string, boolean) => void,
  formData: FormData,
  customerId: string,
  customerEmail: string,
  isDownloading: boolean
  isUpdateCustomerRecordChecked: boolean
  onUpdateCustomerRecordChange: (checked: boolean) => void
  customerNameUpdated: boolean
  hasHehmDevices?: boolean
}

const DownloadSystemReport = ({
  isDownloading,
  resetAndCloseModal,
  devices,
  customerId,
  customerEmail,
  selectEmailSystemReport,
  handleGenerateReport,
  handleFormData,
  formData,
  errors = {},
  isUpdateCustomerRecordChecked,
  onUpdateCustomerRecordChange,
  hasHehmDevices = false,
  customerNameUpdated
}: Props) => {
  const {
    dealer: { imageUrl },
    isDealerLoaded
  } = useDealer()
  const [reportImage, setReportImage] = useState<string | null>(null)
  const { session: { isImpersonating } } = useSessionContext()
  const { hasAccess, LockIcon, showFeature } = usePremiumFeaturesContext()
  const { isUnsubscribed, areUnsubscribedLoaded } = useEmailUnsubscribeStatus(customerEmail, 'system_report')
  const isEmailButtonEnabled = customerEmail && hasAccess('email_system_reports') && !isImpersonating && !isUnsubscribed && areUnsubscribedLoaded && isDealerLoaded
  const screenSize = useScreenSize()
  const isSmallScreen = screenSize === 'small'

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0]
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = async () => {
          const base64Data = (reader.result as string)
          const { imageId } = await Api.SystemReports.uploadReportImage(base64Data, customerId)
          setReportImage(base64Data)
          handleFormData('imageId', imageId)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    setReportImage(scrubNull(imageUrl))
  }, [imageUrl])

  return (
    <>
      <S.FieldWrapper>
        <S.InputWrapper>
          <S.TextInput
            placeholder="First name"
            maxLength={20}
            value={formData.firstName}
            onChange={(event) =>
              handleFormData('firstName', scrubText(event.target.value))
            }
            errorMessage={errors.firstName}
          />
          <S.TextInput
            placeholder="Last name"
            maxLength={20}
            value={formData.lastName}
            onChange={(event) =>
              handleFormData('lastName', scrubText(event.target.value))
            }
            errorMessage={errors.lastName}
          />
        </S.InputWrapper>
        {customerNameUpdated && (
          <S.CustomerRecordCheckbox
            label="Save updated name to Customer Record"
            checked={isUpdateCustomerRecordChecked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onUpdateCustomerRecordChange(event.target.checked)
            }}
          />
        )}
      </S.FieldWrapper>

      {devices.length > 1 && (
        <S.FieldWrapper>
          {(() => {
            const shouldShowDeviceError =
              !!errors?.devices && formData.selectedDeviceIdsAndFileNames.length === 0

            return (
              <>
                <S.FormLabel>
                  Create report for these systems
                  {!isSmallScreen && (
                    <S.ErrorMessage
                      errorMessage={
                        shouldShowDeviceError ? ` (${errors.devices})` : ''
                      }
                    />
                  )}
                </S.FormLabel>

                {isSmallScreen && (
                  <S.ErrorMessage
                    errorMessage={
                      shouldShowDeviceError ? `(${errors.devices})` : ''
                    }
                    className="block-error"
                  />
                )}

                <S.DeviceCheckboxes>
                  {devices.map(({ deviceId }) => (
                    <S.Checkbox
                      key={deviceId}
                      label={deviceId}
                      checked={formData.selectedDeviceIdsAndFileNames.some(item => item.deviceId === deviceId)}
                      onChange={() => {
                        const updatedDeviceIds =
                          formData.selectedDeviceIdsAndFileNames.some(item => item.deviceId === deviceId)
                            ? formData.selectedDeviceIdsAndFileNames.filter(
                              (s) => s.deviceId !== deviceId
                            )
                            : [...formData.selectedDeviceIdsAndFileNames, { deviceId, fileName: '' }]

                        handleFormData('selectedDeviceIdsAndFileNames', updatedDeviceIds)
                      }}
                    />
                  ))}
                </S.DeviceCheckboxes>
              </>
            )
          })()}
        </S.FieldWrapper>
      )}
      <S.FieldWrapper>
        {hasHehmDevices && <S.HehmNotice>Not applicable for EHMKT devices</S.HehmNotice>}
        <S.NotesInput
          value={formData.notes}
          onChange={event => { handleFormData('notes', event.target.value) }}
          label="Add notes to report(s)"
        />
      </S.FieldWrapper>
      {isDealerLoaded
        ? formData.useImageOnFile &&
        <S.ImageContainer>
          { reportImage && <S.Image id="image" alt="dealer logo" src={reportImage} />}
          <input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            accept="image/jpeg, image/png, image/bmp, image/gif"
            data-testid="upload-report-image"
          />
          <label htmlFor="file-upload"> {reportImage ? 'Use a different image for report(s)' : 'Upload dealer logo for report(s)'} </label>
        </S.ImageContainer>
        : <Spinner variant="page" />
      }
      <S.FieldWrapper>
        <S.FormLabel>Include in the report(s):</S.FormLabel>
        <S.Checkbox
          label={`Dealer logo${reportImage ? '' : ' (No logo on account)'}`}
          checked={formData.useImageOnFile && reportImage !== null}
          onChange={() => handleFormData('useImageOnFile', !formData.useImageOnFile)}
          disabled={!reportImage}
        />
        <S.Checkbox
          label="Service Contract status"
          checked={formData.useServiceContractStatus}
          onChange={() => { handleFormData('useServiceContractStatus', !formData.useServiceContractStatus) }}
        />
        <S.Checkbox
          label="Save to Diagnostics account"
          checked={formData.useSaveReportStatus}
          disabled={isImpersonating}
          onChange={() => { handleFormData('useSaveReportStatus', !formData.useSaveReportStatus) }}
        />
        <S.Checkbox
          label="Customer Runtime History Graph (if data available)"
          checked={formData.includeConsumerRthGraph}
          onChange={() => { handleFormData('includeConsumerRthGraph', !formData.includeConsumerRthGraph) }}
        />
        <S.ConsumerRthType
          selectedValue={formData.consumerRthType}
          radioOptions={[
            { label: 'Daily Totals for Last 9 Days', value: 'daily' },
            { label: 'Monthly Totals for Last 14 Months', value: 'monthly' }
          ]}
          onChange={(event) => handleFormData('consumerRthType', event.target.value)}
          disabled={!formData.includeConsumerRthGraph}
        />
      </S.FieldWrapper>

      <S.ButtonContainer>
        <ModalButton onPress={resetAndCloseModal} decoration="textOnly" text="Cancel" />
        <ModalButton showSpinner={isDownloading} onPress={handleGenerateReport} decoration="secondary" text="Download Report" enabled={isDealerLoaded} />
        {showFeature('email_system_reports') &&
          <S.EmailButtonWrapper data-testid="email-report-btn-wrapper">
            <LockIcon featureCode="email_system_reports" featureComponentType={'button'} />
            <ModalButton
              onPress={selectEmailSystemReport}
              decoration="positive"
              text="Email Report"
              enabled={isEmailButtonEnabled}
            />
          </S.EmailButtonWrapper>
        }
      </S.ButtonContainer>
      {isUnsubscribed && <S.Note>*Customer has opted out of receiving system report emails.</S.Note>}
      {hasAccess('email_system_reports') && !customerEmail && <S.Note>*No Customer email on file.</S.Note>}
    </>
  )
}

export default DownloadSystemReport

const S = {
  FieldWrapper: styled.div`
    > span{
      margin-bottom: 4px;
      display: block;
    }
    margin-bottom: 10px;
  `,
  InputWrapper: styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  `,
  TextInput: styled(TextInput)`
    width: calc(50% - 5px);
    &:focus{
      border: 1px solid ${color('primaryButton')};
      box-shadow: none;
    }
  `,
  DeviceCheckboxes: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    @media ${screenSize.small} {
      grid-template-columns: 1fr 1fr;
    }
  `,
  Checkbox: styled(Checkbox)`
    padding: 4px 0px;
    border-width: 2px;
    margin-bottom: 4px;
    margin-left: 15px;
    > label{
      ${({ disabled }) => !disabled && `color: ${color('checkboxText')};`}
    }
  `,
  ErrorMessage: styled(ErrorMessage)`
    font-weight: normal;
    font-size: 14px;
    float: none;

    &.block-error {
      display: block;
    }
  `,
  EmailButtonWrapper: styled.span`
    position: relative;
  `,
  NotesInput: styled(Textarea)`
    display: flex;
    flex-direction: column;
    > label{
      color: ${color('primaryText')};
    }
  `,
  HehmNotice: styled.p`
    margin: 0 0 8px;
    font-size: 13px;
    color: ${color('secondaryText')};
    font-style: italic;
  `,
  ImageContainer: styled.div`
    height: 180px;
    width: 220px;
    margin: 0 auto;
    text-align: center;
    & input{
      visibility: hidden;
      width: 100%;
    }
    label{
      cursor: pointer;
      color: ${color('primaryButton')};
      text-decoration: underline;
      font-size: 12px;
      font-weight: 600;
      position: relative;
      top: -20px;
      padding: 4px 0px;
    }
  `,
  Image: styled.img`
    border: 1px solid ${color('border')};
    width: 100%;
    height: 120px;
    object-fit: contain;
  `,
  OkButtonLink: styled.a`
    margin-left: 10px;
    display: flex;
  `,
  ButtonContainer: styled(ButtonContainer)`
    border: none;
    align-items: center;
    gap: 5px;
  `,
  FormLabel: styled.label`
    color: ${color('primaryText')};
  `,
  ConsumerRthType: styled(RadioInputs)`
    flex-direction: column;
    color: ${color('inputOptionText')};
    margin-left: 35px;
    margin-top: 4px;
    margin-bottom: 4px;

    > div {
      margin-bottom: 4px;
    }
  `,
  Note: styled.label`
    margin-top: 6px;
    display: block;
    text-align: right;
  `,
  CustomerRecordCheckbox: styled(Checkbox)`
    width: 100%;

    input[type='checkbox'] {
      margin: 2px 10px 0 0;
      cursor: pointer;
    }
  `
}
